import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { axios } from 'api'
import { EventsPage } from 'components_v2/Events'
import { useMeta, useTitle } from 'hooks/useMetaTitle'
import { TIMEZONE, useCommonEntryContext } from 'components_v2/common'
import {
  getCountryCode,
  getVisitorRegionCandidates
} from 'helpers/getCountryCode'
import {
  isSameRegionSelection,
  matchesUserCountry,
  matchesUserRegion
} from './countryFilter'

const readStoredRegion = () => {
  if (typeof window === 'undefined') return null
  try {
    const item = localStorage.getItem('selected_event_region')
    if (!item || item === 'all_regions') return null
    return JSON.parse(item)
  } catch {
    return null
  }
}

const buildEventParams = ({ section, period, country, page = 1 }) => {
  const params = new URLSearchParams({
    page: String(page),
    per_page: 12,
    timezone: TIMEZONE
  })

  if (section) {
    params.set('sections', section === 'myEvents' ? 'my_events' : section)
  }

  if (!section || section === 'upcoming') {
    if (period === 'this_week') params.set('period', 'this_week')
    if (country?.country) params.set('country', country.country)
    if (country?.region) params.set('region', country.region)
  }

  return params.toString()
}

const EventsView = () => {
  const { globalState, setGlobalState } = useCommonEntryContext()
  const selected_event_region = globalState?.selected_event_region

  const selectedRegion = useMemo(() => {
    if (selected_event_region) return selected_event_region
    return readStoredRegion()
  }, [selected_event_region])

  const [eventsData, setEvents] = useState({})
  const [selectedFilter, setSelectedFilter] = useState('upcoming')
  const [isError, setIsError] = useState(false)
  const [paginationLoading, setPaginationLoading] = useState(false)
  const [currentPages, setCurrentPages] = useState({
    myEvents: 1,
    upcoming: 1,
    past: 1
  })

  const latestRequestRef = useRef(0)
  const userChangedRegionRef = useRef(false)
  const initialLoadStartedRef = useRef(false)

  useTitle('Community Events')
  useMeta([
    {
      name: 'description',
      content: 'Discover upcoming ALO Wellness Club community events.'
    }
  ])

  const fetchAllEvents = useCallback(async (period, region, load = false) => {
    const requestId = ++latestRequestRef.current

    try {
      const queryString = buildEventParams({ period, country: region })
      const res = await axios.get(`/events?${queryString}`, {
        load: load === true
      })
      if (requestId !== latestRequestRef.current) return

      setEvents(res?.data || {})
      setCurrentPages({ myEvents: 1, upcoming: 1, past: 1 })
      setIsError(false)
    } catch (err) {
      if (requestId !== latestRequestRef.current) return
      console.error('Failed to fetch all events:', err)
      setIsError(true)
      setEvents({})
    }
  }, [])

  const fetchUpcoming = useCallback(async (period, region) => {
    const requestId = ++latestRequestRef.current

    try {
      const queryString = buildEventParams({
        section: 'upcoming',
        period,
        country: region
      })
      const res = await axios.get(`/events?${queryString}`)
      if (requestId !== latestRequestRef.current) return

      setEvents((prev) => ({ ...prev, ...(res?.data || {}) }))
      setCurrentPages((prev) => ({ ...prev, upcoming: 1 }))
      setIsError(false)
    } catch (err) {
      if (requestId !== latestRequestRef.current) return
      console.error('Failed to fetch upcoming events:', err)
      setIsError(true)
    }
  }, [])

  // Initial load runs once; geo may set default region without re-running this effect.
  useEffect(() => {
    if (initialLoadStartedRef.current) return undefined
    initialLoadStartedRef.current = true

    let cancelled = false
    ;(async () => {
      let matchedRegion = readStoredRegion()

      try {
        if (!matchedRegion) {
          const [visitorRegions, userCountry, regionsRes] = await Promise.all([
            getVisitorRegionCandidates(),
            getCountryCode(),
            axios.get('/events/regions')
          ])
          if (cancelled) return

          const regions = regionsRes.data?.regions || []
          const regionMatches = regions.filter((entry) =>
            visitorRegions.some((candidate) =>
              matchesUserRegion(entry.region, candidate)
            )
          )

          matchedRegion =
            regionMatches.find((entry) =>
              matchesUserCountry(entry.country, userCountry)
            ) || regionMatches[0]

          if (matchedRegion && !userChangedRegionRef.current) {
            setGlobalState('selected_event_region', matchedRegion)
            localStorage.setItem(
              'selected_event_region',
              JSON.stringify(matchedRegion)
            )
          }
        }
      } catch (err) {
        console.error('Geo detection fallback:', err)
      }

      if (!cancelled && !userChangedRegionRef.current) {
        fetchAllEvents('upcoming', matchedRegion, true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [fetchAllEvents, setGlobalState])

  const handleFilterChange = useCallback(
    (filter) => {
      if (filter === selectedFilter) return
      setSelectedFilter(filter)
      fetchUpcoming(filter, selectedRegion)
    },
    [fetchUpcoming, selectedFilter, selectedRegion]
  )

  const handleRegionChange = useCallback(
    (regionEntry) => {
      if (isSameRegionSelection(regionEntry, selectedRegion)) return

      userChangedRegionRef.current = true
      setGlobalState('selected_event_region', regionEntry)
      localStorage.setItem(
        'selected_event_region',
        regionEntry ? JSON.stringify(regionEntry) : 'all_regions'
      )
      fetchUpcoming(selectedFilter, regionEntry)
    },
    [fetchUpcoming, selectedFilter, selectedRegion, setGlobalState]
  )

  const handleRegistrationChange = useCallback(() => {
    fetchAllEvents(selectedFilter, selectedRegion, false)
  }, [fetchAllEvents, selectedFilter, selectedRegion])

  const loadNextPageData = async ({
    section,
    pageNumber,
    callBack = () => {}
  }) => {
    if (paginationLoading) return
    setPaginationLoading(true)

    try {
      const queryString = buildEventParams({
        section,
        period: selectedFilter,
        country: selectedRegion,
        page: pageNumber
      })

      const res = await axios.get(`/events?${queryString}`)
      const nextPageData = res?.data?.[section] || {}

      setEvents((prev) => ({
        ...prev,
        [section]: {
          ...nextPageData,
          data: [
            ...(prev?.[section]?.data || []),
            ...(nextPageData?.data || [])
          ]
        }
      }))

      setCurrentPages((prev) => ({ ...prev, [section]: pageNumber }))
      setTimeout(() => callBack(), 500)
    } catch (err) {
      console.error('Failed to paginate:', err)
      setIsError(true)
    } finally {
      setPaginationLoading(false)
    }
  }

  return (
    <EventsPage
      eventsData={eventsData}
      isLoading={Object.keys(eventsData).length === 0 && !isError}
      isError={isError}
      onRegistrationChange={handleRegistrationChange}
      selectedFilter={selectedFilter}
      setSelectedFilter={handleFilterChange}
      selectedRegion={selectedRegion}
      setSelectedRegion={handleRegionChange}
      loadNextPageData={loadNextPageData}
      currentPageMine={currentPages.myEvents}
      currentPageUpcoming={currentPages.upcoming}
      currentPagePast={currentPages.past}
      paginationLloading={paginationLoading}
      setPaginationLloading={setPaginationLoading}
    />
  )
}

export default EventsView
